// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { checkboxSizes, CheckboxStyle } from "@insite/mobius/Checkbox";
import CheckboxGroupContext from "@insite/mobius/CheckboxGroup/CheckboxGroupContext";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { xFieldValidator, xValidationProps } from "@synd/XDL/Utilities";
import * as React from "react";
import styled, { ThemeProps, withTheme } from "styled-components";

export interface CheckboxGroupPresentationProps {
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<CheckboxGroupProps>;
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
}

export type CheckboxGroupComponentProps = MobiusStyledComponentProps<
    "fieldset",
    {
        /** Error message to be displayed below the CheckboxGroup. */
        error?: React.ReactNode;
        /** Label to be displayed above the CheckboxGroup. */
        label?: React.ReactNode;
        /** Adds an asterisk to the label (if provided). */
        required?: boolean;
        uid?: string;
        /**
         * XNG-Changes: Value prop for validation.
         * Pass the current selected value to enable validation using xFieldValidator.
         */
        value?: string | boolean | number | null;
    }
>;

export type CheckboxGroupProps = FieldSetGroupPresentationProps<CheckboxGroupComponentProps> &
    CheckboxGroupComponentProps &
    CheckboxGroupPresentationProps &
    // XNG-Changes
    xValidationProps;

const CheckboxGroupStyle = styled.fieldset<InjectableCss>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    border: 0;
    padding: 0;
    margin: 0;
    & > ${TypographyStyle as any} {
        padding: 0;
        &:first-child {
            margin-bottom: 10px;
        }
        &:last-child {
            margin-top: 10px;
        }
    }
    ${/* sc-selector */ CheckboxStyle} + ${/* sc-selector */ CheckboxStyle} {
        margin-top: 10px;
    }
    [data-checkbox-only] {
        margin-top: 4px;
    }
    ${injectCss}
`;

interface CheckboxGroupState {
    uid: string;
    internalError: string;
}

/**
 * CheckboxGroup provides spacing and alignment styling for multiple checkboxes in a form group, as well as providing
 * a label for the group of inputs and functionality for when the checkbox is required.
 *
 * XNG-Changes: Added custom validation with xFieldValidator.
 */
class CheckboxGroup extends React.Component<CheckboxGroupProps & ThemeProps<BaseTheme>, CheckboxGroupState> {
    state: CheckboxGroupState = {
        uid: this.props.uid || uniqueId(),
        // XNG-Changes: validation error message
        internalError: "",
    };

    // XNG-Changes
    private setInternalError = (error: string) => {
        this.setState({ internalError: error });
    };

    // XNG-Changes: Clear error when value changes (checkbox is clicked)
    componentDidUpdate(prevProps: CheckboxGroupProps) {
        if (this.props.value !== prevProps.value && this.props.value && this.state.internalError) {
            this.setState({ internalError: "" });
        }
    }

    render() {
        const {
            children,
            error: errorProp,
            label,
            required,
            mergeCss,
            // XNG-Changes: validation props
            formId,
            value,
            validationRules,
            disableValidation,
            messageLabel,
            ...otherProps
        } = this.props;

        // XNG-Changes: Register with xFieldValidator if formId is provided
        if (formId && !disableValidation) {
            xFieldValidator(
                {
                    label: (messageLabel || label) as string,
                    type: "text",
                    validationRules,
                    value: value ?? "",
                    onError: this.setInternalError,
                    required,
                },
                formId,
                disableValidation,
            );
        }

        // XNG-Changes
        const error = errorProp || this.state.internalError;

        const { applyProp, spreadProps, applyStyledProp } = applyPropBuilder(this.props, {
            component: "checkbox",
            category: "fieldSet",
            propKey: "groupDefaultProps",
        });

        const sizeVariant = applyProp("sizeVariant", "default") as Required<
            Pick<CheckboxGroupProps, "sizeVariant">
        >["sizeVariant"];

        const labelProps: { "aria-labelledby"?: string; as?: "div" } = {
            "aria-labelledby": this.state.uid,
            as: typeof children === "object" ? "div" : undefined,
        };

        const resolvedMergeCss = mergeCss ?? this.props?.theme?.checkbox?.groupDefaultProps?.mergeCss;

        return (
            <CheckboxGroupStyle
                {...labelProps}
                {...omitMultiple(otherProps, ["uid", "sizeVariant", "css"])}
                css={applyStyledProp("css", resolvedMergeCss)}
                role="group"
            >
                {String(label) && (
                    <Typography
                        as="legend"
                        weight={600}
                        size={checkboxSizes[sizeVariant].fontSize}
                        id={this.state.uid}
                        {...spreadProps("labelProps" as any)}
                    >
                        {label}
                        {required && " *"}
                    </Typography>
                )}
                <CheckboxGroupContext.Provider
                    value={{
                        sizeVariant,
                    }}
                >
                    {children}
                </CheckboxGroupContext.Provider>
                {String(error) && (
                    <Typography
                        color="danger"
                        // XNG-Changes: set weight to regular
                        weight={`${fontConfig.weight.regular}`}
                        size={checkboxSizes[sizeVariant].fontSize}
                        {...spreadProps("errorProps" as any)}
                    >
                        {error}
                    </Typography>
                )}
            </CheckboxGroupStyle>
        );
    }
}

/** @component */
export default withTheme(CheckboxGroup);

export { CheckboxGroupStyle };
